// FILE: src/services/assistant/types/AssistantTypes.ts
// VERSION: Modified to include UserProfile in AssistantContext and add CHEFCOIN_QUERY intent

import { Recipe, RecipeFormData } from '../../../types/recipe';
import { UserProfile } from '../../../types/user'; // <-- ADD: Import the UserProfile type

/**
 * Core assistant intent enumeration
 * Defines all possible user intents that the assistant can handle
 */
export enum AssistantIntent {
  SEARCH_RECIPES = 'SEARCH_RECIPES',
  RECIPE_QUESTION = 'RECIPE_QUESTION',
  IMAGE_REQUEST = 'IMAGE_REQUEST',
  RECIPE_CREATION = 'RECIPE_CREATION',
  RECIPE_IMPORT_READY = 'RECIPE_IMPORT_READY',
  RECIPE_IMPORT_CONFIRM = 'RECIPE_IMPORT_CONFIRM',
  RECIPE_COPY = 'RECIPE_COPY',
  RECIPE_MODIFY = 'RECIPE_MODIFY',
  STUDIO_OPEN = 'STUDIO_OPEN',
  GENERAL = 'GENERAL',
  CHEFCOIN_QUERY = 'CHEFCOIN_QUERY'
}

/**
 * Assistant action enumeration
 * Defines all possible actions that can be triggered via action buttons
 */
export enum AssistantAction {
  GENERATE_IMAGE = 'generate-image',
  REGENERATE_IMAGE = 'regenerate-image',
  EDIT_IMAGE_PROMPT = 'edit-image-prompt',
  ACCEPT_IMAGE = 'accept-image',
  DOWNLOAD_HIGHRES = 'download-highres',
  GENERATE_RECIPE_IMAGE = 'generate-recipe-image',
  OPEN_RECIPE_STUDIO = 'open-recipe-studio',
  CONFIRM_RECIPE_IMPORT = 'confirm-recipe-import',
  OPEN_PASTE_FORM = 'open-paste-form'
}

/**
 * Message interface for conversation history
 */
export interface Message {
  content: string;
  isUser: boolean;
  text: string;
  sender: 'user' | 'assistant';
  metadata?: any;
}

/**
 * Context provided to command handlers
 */
export interface AssistantContext {
  userMessage: string;
  currentRecipe?: Recipe | null; // <-- MODIFIED: Explicitly allow null
  conversationHistory?: Message[];
  actionData?: Record<string, string>;
  activeConversationId?: string;
  userProfile?: UserProfile | null; // <-- ADD: The user's complete profile, including coin balance
}

/**
 * Standard response format for all assistant operations
 */
export interface AssistantResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
  metadata?: {
    type?: string;
    formData?: Partial<RecipeFormData>;
    structuredMarkdown?: string;
    originalTitle?: string;
    changesSummary?: string;
    modifiedFormData?: Partial<RecipeFormData>;
    shouldOpenStudio?: boolean;
  };
}

/**
 * Command handler function signature
 */
export type CommandHandler = (context: AssistantContext) => Promise<AssistantResponse>;

/**
 * Action handler function signature
 */
export type ActionHandler = (action: AssistantAction, dataAttrs: Record<string, string>, context: AssistantContext) => Promise<AssistantResponse>;

/**
 * Intent detection result
 */
export interface IntentDetectionResult {
  intent: AssistantIntent;
  confidence: number;
  triggers: string[];
  metadata?: Record<string, any>;
}

/**
 * Command execution result with additional metadata
 */
export interface CommandExecutionResult extends AssistantResponse {
  intent: AssistantIntent;
  executionTime: number;
  commandId: string;
}

/**
 * Action execution result with additional metadata
 */
export interface ActionExecutionResult extends AssistantResponse {
  action: AssistantAction;
  executionTime: number;
  actionId: string;
}